import * as echarts from 'echarts/dist/echarts.esm.js';
import type { ECharts, EChartsOption, SetOptionOpts } from 'echarts/types/dist/echarts';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

export interface ReactEChartsProps {
    option: EChartsOption;
    style?: CSSProperties;
    settings?: SetOptionOpts;
    loading?: boolean;
    theme?: 'light' | 'dark';
}

export function ReactECharts({ option, style, settings, loading, theme }: ReactEChartsProps): JSX.Element {
    const chartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // initialize chart
        let chart: ECharts | undefined;
        if (chartRef.current !== null) {
            chart = echarts.init(chartRef.current, theme);
        }

        // chart resize listener
        function resizeChart() {
            chart?.resize();
        }
        window.addEventListener('resize', resizeChart);

        // cleanup
        return () => {
            chart?.dispose();
            window.removeEventListener('resize', resizeChart);
        };
    }, [theme]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        // update chart
        if (chartRef.current !== null) {
            const chart = echarts.getInstanceByDom(chartRef.current);
            chart.setOption(option, settings);
        }
        // When theme changes we need to add option and setting due to it being deleted in cleanup function
    }, [option, settings, theme]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        // update chart
        if (chartRef.current !== null) {
            const chart = echarts.getInstanceByDom(chartRef.current);
            loading === true ? chart.showLoading() : chart.hideLoading();
        }
    }, [loading, theme]);

    return <div ref={chartRef} style={{ width: '100%', height: '100%', ...style }} />;
}
